// frontend/src/features/seo/components/ContentGraphTab.js
import React, { useState, useMemo, useEffect } from 'react';
import axiosInstance from '../../../api/axios';

export const EMPTY_CONTENT_GRAPH = {
  nodes: [],
  edges: [],
  recommendedLinks: [],
};

/**
 * Detects whether a graph object contains legacy hardcoded mock nodes.
 */
export function isLegacyMockGraph(g) {
  if (!g || !Array.isArray(g.nodes) || g.nodes.length === 0) return false;
  const legacyUrls = new Set([
    '/blog/aieo-guide-2026',
    '/guides/b2b-lead-enrichment-comparison',
    '/blog/cold-email-deliverability-2026',
    '/features/campaign',
    '/features/ai-content-studio',
    '/customer-management',
    '/pricing',
  ]);
  const hasLegacyNodeId = g.nodes.some(
    (n) =>
      (n.id && /^node-[1-7]$/.test(n.id) && legacyUrls.has(n.url)) ||
      (n.url &&
        (n.url === '/blog/aieo-guide-2026' || n.url === '/guides/b2b-lead-enrichment-comparison'))
  );
  const matchCount = g.nodes.filter((n) => legacyUrls.has(n.url)).length;
  return hasLegacyNodeId || matchCount >= 4;
}

/**
 * Automatically synchronizes workspace briefs and their internalLinks into graph nodes and edges.
 * Preserves user-created custom nodes and custom edges.
 */
/**
 * Automatically synchronizes workspace briefs and their internalLinks into graph nodes and edges.
 * Preserves user-created custom nodes and custom edges.
 * Enriches graph nodes with topic cluster, entity, and semantic metadata (FR-M11-007).
 */
export function syncGraphWithBriefs(currentGraph, workspaceBriefs = [], topicClusters = []) {
  const safeBriefs = Array.isArray(workspaceBriefs) ? workspaceBriefs : [];
  const existingNodes = Array.isArray(currentGraph?.nodes) ? [...currentGraph.nodes] : [];
  let existingEdges = Array.isArray(currentGraph?.edges) ? [...currentGraph.edges] : [];
  const recs = Array.isArray(currentGraph?.recommendedLinks)
    ? [...currentGraph.recommendedLinks]
    : [];

  let changed = false;

  // 1. Prune brief nodes whose briefId no longer exists in workspaceBriefs
  const validBriefIds = new Set(safeBriefs.map((b) => b.id));
  const removedNodeIds = new Set();
  const retainedNodes = existingNodes.filter((n) => {
    if (n.briefId && !validBriefIds.has(n.briefId)) {
      removedNodeIds.add(n.id);
      changed = true;
      return false;
    }
    return true;
  });

  if (removedNodeIds.size > 0) {
    existingEdges = existingEdges.filter(
      (e) => !removedNodeIds.has(e.source) && !removedNodeIds.has(e.target)
    );
  }

  const urlMap = new Map();
  const idMap = new Map();
  retainedNodes.forEach((n) => {
    if (n.url) urlMap.set(n.url.toLowerCase(), n);
    if (n.id) idMap.set(n.id, n);
  });

  // 2. Sync each active workspace brief as an indexed page node
  safeBriefs.forEach((b) => {
    const slug = (
      b.targetSlug ||
      `/blog/${(b.title || 'untitled')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')}`
    ).toLowerCase();
    const cleanUrl = slug.startsWith('/') ? slug : `/${slug}`;

    const matchedCluster = (topicClusters || []).find(
      (c) =>
        c.id === b.topicClusterId ||
        (c.topic && b.title && b.title.toLowerCase().includes(c.topic.toLowerCase()))
    );
    const topic = matchedCluster?.topic || b.topic || b.title;
    const clusterId = b.topicClusterId || matchedCluster?.id || null;
    const entities =
      Array.isArray(b.targetEntities) && b.targetEntities.length > 0
        ? b.targetEntities
        : matchedCluster?.entities || [];
    const authority = b.status === 'approved' ? 85 : 75;

    let node = retainedNodes.find((n) => n.briefId === b.id) || urlMap.get(cleanUrl);

    if (node) {
      if (
        node.title !== b.title ||
        node.url !== cleanUrl ||
        node.briefId !== b.id ||
        node.status !== b.status ||
        node.authority !== authority ||
        node.topic !== topic ||
        node.topicClusterId !== clusterId ||
        JSON.stringify(node.entities || []) !== JSON.stringify(entities)
      ) {
        node.title = b.title;
        node.url = cleanUrl;
        node.briefId = b.id;
        node.status = b.status || 'draft';
        node.authority = authority;
        node.topic = topic;
        node.topicClusterId = clusterId;
        node.entities = entities;
        changed = true;
      }
      urlMap.set(cleanUrl, node);
      idMap.set(node.id, node);
    } else {
      const newNode = {
        id: `node-${b.id}`,
        briefId: b.id,
        title: b.title,
        url: cleanUrl,
        type: cleanUrl.includes('/guide') ? 'guide' : 'blog',
        status: b.status || 'draft',
        authority,
        topic,
        topicClusterId: clusterId,
        entities,
      };
      retainedNodes.push(newNode);
      urlMap.set(cleanUrl, newNode);
      idMap.set(newNode.id, newNode);
      changed = true;
    }
  });

  // 3. Sync internalLinks declared inside active briefs
  safeBriefs.forEach((b) => {
    const slug = (
      b.targetSlug ||
      `/blog/${(b.title || 'untitled')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')}`
    ).toLowerCase();
    const cleanUrl = slug.startsWith('/') ? slug : `/${slug}`;
    const sourceNode = retainedNodes.find((n) => n.briefId === b.id) || urlMap.get(cleanUrl);
    if (!sourceNode) return;

    const declaredTargetUrls = new Set();

    if (Array.isArray(b.internalLinks)) {
      b.internalLinks.forEach((link) => {
        const rawTarget = (link.page || '').trim();
        if (!rawTarget) return;
        const isExternal = /^https?:\/\//i.test(rawTarget) || rawTarget.startsWith('www.');
        const targetUrl = isExternal
          ? rawTarget.toLowerCase()
          : (rawTarget.startsWith('/') ? rawTarget : `/${rawTarget}`).toLowerCase();

        declaredTargetUrls.add(targetUrl);

        let targetNode = urlMap.get(targetUrl);
        if (!targetNode) {
          let title = '';
          let pType = 'product';

          if (isExternal) {
            pType = 'external';
            const cleanDomain = targetUrl
              .replace(/^https?:\/\//i, '')
              .replace(/^www\./i, '')
              .split('/')[0];
            title = link.anchorText ? `${cleanDomain} (${link.anchorText})` : cleanDomain;
          } else {
            const slugParts = targetUrl.split('/').filter(Boolean);
            const lastPart = slugParts[slugParts.length - 1] || 'page';
            title = lastPart
              .split('-')
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' ');

            if (targetUrl.includes('pricing') || targetUrl.includes('commercial'))
              pType = 'commercial';
            else if (targetUrl.includes('guide')) pType = 'guide';
            else if (targetUrl.includes('blog')) pType = 'blog';
          }

          const targetId = isExternal
            ? `node-ext-${targetUrl.replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`
            : `node-${
                targetUrl
                  .split('/')
                  .filter(Boolean)
                  .pop()
                  ?.replace(/[^a-z0-9]+/g, '-') || 'target'
              }`;

          targetNode = {
            id: targetId,
            title,
            url: targetUrl,
            type: pType,
            authority: isExternal ? 85 : 80,
            isTargetPage: true,
            isExternal,
            topic: sourceNode.topic || title,
            topicClusterId: sourceNode.topicClusterId || null,
          };
          retainedNodes.push(targetNode);
          urlMap.set(targetUrl, targetNode);
          idMap.set(targetNode.id, targetNode);
          changed = true;
        }

        if (sourceNode.id !== targetNode.id) {
          const edgeExists = existingEdges.some(
            (e) => e.source === sourceNode.id && e.target === targetNode.id
          );
          if (!edgeExists) {
            existingEdges.push({
              source: sourceNode.id,
              target: targetNode.id,
              anchor: link.anchorText || (isExternal ? 'external reference' : 'relevant resource'),
            });
            changed = true;
          }
        }
      });
    }

    // Remove edges from this sourceNode that were linked to auto-generated target pages no longer declared
    const staleBriefEdges = existingEdges.filter((e) => {
      if (e.source !== sourceNode.id) return false;
      const targetNode = idMap.get(e.target);
      if (!targetNode) return true; // dangling edge
      if (targetNode.isTargetPage && !declaredTargetUrls.has(targetNode.url.toLowerCase())) {
        return true;
      }
      return false;
    });

    if (staleBriefEdges.length > 0) {
      const staleSet = new Set(staleBriefEdges);
      existingEdges = existingEdges.filter((e) => !staleSet.has(e));
      changed = true;
    }
  });

  // Prune any auto-created target pages that have no incoming edges
  const targetPagesPruned = retainedNodes.filter((n) => {
    if (!n.isTargetPage) return true;
    const hasIncomingEdge = existingEdges.some((e) => e.target === n.id);
    if (!hasIncomingEdge) {
      changed = true;
      return false;
    }
    return true;
  });

  return changed
    ? { nodes: targetPagesPruned, edges: existingEdges, recommendedLinks: recs }
    : currentGraph;
}

export default function ContentGraphTab({
  contentGraph: propGraph,
  onUpdateGraph,
  briefs = [],
  topicClusters = [],
}) {
  const prevPropGraphRef = React.useRef(propGraph);
  const prevBriefsRef = React.useRef(briefs);
  const prevClustersRef = React.useRef(topicClusters);

  // Internal persistent graph state (purges legacy mock data on startup and syncs briefs)
  const [graph, setGraph] = useState(() => {
    let initial = EMPTY_CONTENT_GRAPH;
    if (propGraph && Array.isArray(propGraph.nodes) && propGraph.nodes.length > 0) {
      initial = isLegacyMockGraph(propGraph) ? EMPTY_CONTENT_GRAPH : propGraph;
    } else {
      try {
        const saved = localStorage.getItem('lead_huntrix_seo_content_graph');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (isLegacyMockGraph(parsed)) {
            localStorage.removeItem('lead_huntrix_seo_content_graph');
            initial = EMPTY_CONTENT_GRAPH;
          } else {
            initial = parsed;
          }
        }
      } catch {
        initial = EMPTY_CONTENT_GRAPH;
      }
    }
    return syncGraphWithBriefs(initial, briefs, topicClusters);
  });

  // Keep graph state in sync when briefs, topicClusters, or propGraph change
  useEffect(() => {
    const propGraphChanged = prevPropGraphRef.current !== propGraph;
    const briefsChanged = prevBriefsRef.current !== briefs;
    const clustersChanged = prevClustersRef.current !== topicClusters;

    if (!propGraphChanged && !briefsChanged && !clustersChanged) {
      return;
    }

    prevPropGraphRef.current = propGraph;
    prevBriefsRef.current = briefs;
    prevClustersRef.current = topicClusters;

    setGraph((prev) => {
      const base =
        propGraphChanged &&
        propGraph &&
        Array.isArray(propGraph.nodes) &&
        !isLegacyMockGraph(propGraph)
          ? propGraph
          : prev;

      const synced = syncGraphWithBriefs(base, briefs, topicClusters);
      if (synced !== base && synced !== prev) {
        if (onUpdateGraph) {
          onUpdateGraph(synced);
        } else {
          try {
            localStorage.setItem('lead_huntrix_seo_content_graph', JSON.stringify(synced));
          } catch (e) {
            console.warn('Failed to save content graph to localStorage', e);
          }
        }
        return synced;
      }
      return prev;
    });
  }, [propGraph, briefs, topicClusters, onUpdateGraph]);

  const updateGraph = (updated) => {
    setGraph(updated);
    if (onUpdateGraph) {
      onUpdateGraph(updated);
    } else {
      try {
        localStorage.setItem('lead_huntrix_seo_content_graph', JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save content graph to localStorage', e);
      }
    }
  };

  // View mode: 'split' (cards & details) or 'canvas' (interactive visual SVG graph)
  const [viewMode, setViewMode] = useState('split');

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [topologyFilter, setTopologyFilter] = useState('all'); // 'all' | 'orphan' | 'hub' | 'deadend'
  const [recScope, setRecScope] = useState('selected'); // 'selected' | 'all'
  const [briefsSectionCollapsed, setBriefsSectionCollapsed] = useState(false);
  const [targetsSectionCollapsed, setTargetsSectionCollapsed] = useState(false);

  // Selected node
  const [selectedNodeId, setSelectedNodeId] = useState(() => graph.nodes?.[0]?.id || null);

  // Status notification / toast
  const [notification, setNotification] = useState(null);

  // AI Recommendation loading state
  const [isGeneratingRecs, setIsGeneratingRecs] = useState(false);

  // Modals state
  const [isAddNodeOpen, setIsAddNodeOpen] = useState(false);
  const [isEditNodeOpen, setIsEditNodeOpen] = useState(false);
  const [isAddLinkOpen, setIsAddLinkOpen] = useState(false);

  // Add Node Form
  const [newNodeTitle, setNewNodeTitle] = useState('');
  const [newNodeUrl, setNewNodeUrl] = useState('');
  const [newNodeType, setNewNodeType] = useState('blog');
  const [newNodeAuthority, setNewNodeAuthority] = useState(75);
  const [newNodeTopic, setNewNodeTopic] = useState('');

  // Edit Node Form
  const [editNodeTitle, setEditNodeTitle] = useState('');
  const [editNodeUrl, setEditNodeUrl] = useState('');
  const [editNodeType, setEditNodeType] = useState('blog');
  const [editNodeAuthority, setEditNodeAuthority] = useState(75);
  const [editNodeTopic, setEditNodeTopic] = useState('');

  // Add Link Form
  const [newLinkTargetId, setNewLinkTargetId] = useState('');
  const [newLinkAnchor, setNewLinkAnchor] = useState('');

  // Auto-dismiss notification after 4s
  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(() => setNotification(null), 4000);
    return () => clearTimeout(timer);
  }, [notification]);

  // Compute live In-Degree and Out-Degree Maps from active edges
  const inDegreeMap = useMemo(() => {
    const map = {};
    (graph.nodes || []).forEach((n) => {
      map[n.id] = 0;
    });
    (graph.edges || []).forEach((e) => {
      if (map[e.target] !== undefined) map[e.target] += 1;
    });
    return map;
  }, [graph.nodes, graph.edges]);

  const outDegreeMap = useMemo(() => {
    const map = {};
    (graph.nodes || []).forEach((n) => {
      map[n.id] = 0;
    });
    (graph.edges || []).forEach((e) => {
      if (map[e.source] !== undefined) map[e.source] += 1;
    });
    return map;
  }, [graph.nodes, graph.edges]);

  // Identify Orphan pages (0 inbound links) and Dead Ends (0 outbound links)
  const orphanNodes = useMemo(() => {
    return (graph.nodes || []).filter((n) => (inDegreeMap[n.id] || 0) === 0);
  }, [graph.nodes, inDegreeMap]);

  const deadEndNodes = useMemo(() => {
    return (graph.nodes || []).filter((n) => (outDegreeMap[n.id] || 0) === 0);
  }, [graph.nodes, outDegreeMap]);

  // Real-Time Circular Loop / Reciprocal Cycle Detection (FR-M11-007)
  const detectedLoops = useMemo(() => {
    const loops = [];
    const edgeSet = new Set((graph.edges || []).map((e) => `${e.source}->${e.target}`));
    const seen = new Set();

    (graph.edges || []).forEach((e) => {
      // Direct self-loop
      if (e.source === e.target) {
        loops.push({
          key: `self-${e.source}`,
          type: 'self',
          source: e.source,
          target: e.target,
          description: `Self-linking loop on ${e.source}`,
        });
      }
      // Reciprocal 2-node cycle
      else if (edgeSet.has(`${e.target}->${e.source}`)) {
        const key = [e.source, e.target].sort().join('<->');
        if (!seen.has(key)) {
          seen.add(key);
          loops.push({
            key,
            type: 'reciprocal',
            nodeA: e.source,
            nodeB: e.target,
            description: `Reciprocal circular link loop between ${e.source} and ${e.target}`,
          });
        }
      }
    });

    return loops;
  }, [graph.edges]);

  // Find currently selected node object
  const selectedNode = useMemo(() => {
    return (graph.nodes || []).find((n) => n.id === selectedNodeId) || graph.nodes?.[0] || null;
  }, [graph.nodes, selectedNodeId]);

  // Sync edit form state when selectedNode changes
  useEffect(() => {
    if (selectedNode) {
      setEditNodeTitle(selectedNode.title);
      setEditNodeUrl(selectedNode.url);
      setEditNodeType(selectedNode.type);
      setEditNodeAuthority(selectedNode.authority || 75);
      setEditNodeTopic(selectedNode.topic || '');
    }
  }, [selectedNode]);

  // Outgoing and Incoming edges for the currently selected node
  const nodeOutgoingEdges = useMemo(() => {
    return (graph.edges || []).filter((e) => e.source === selectedNode?.id);
  }, [graph.edges, selectedNode]);

  const nodeIncomingEdges = useMemo(() => {
    return (graph.edges || []).filter((e) => e.target === selectedNode?.id);
  }, [graph.edges, selectedNode]);

  // Unsynced workspace briefs (briefs in workspace that aren't yet in graph)
  const unsyncedBriefs = useMemo(() => {
    if (!Array.isArray(briefs) || briefs.length === 0) return [];
    return briefs.filter((b) => {
      const slug = (
        b.targetSlug ||
        `/blog/${(b.title || 'untitled')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')}`
      ).toLowerCase();
      const cleanUrl = slug.startsWith('/') ? slug : `/${slug}`;
      const existsInGraph = (graph.nodes || []).some(
        (n) => n.briefId === b.id || n.url?.toLowerCase() === cleanUrl
      );
      return !existsInGraph;
    });
  }, [briefs, graph.nodes]);

  // Filtered nodes list based on search, type, and topology
  const filteredNodes = useMemo(() => {
    return (graph.nodes || []).filter((node) => {
      const matchesSearch =
        node.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.url.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (node.topic && node.topic.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesType = typeFilter === 'all' || node.type === typeFilter;

      let matchesTopology = true;
      if (topologyFilter === 'orphan') {
        matchesTopology = (inDegreeMap[node.id] || 0) === 0;
      } else if (topologyFilter === 'deadend') {
        matchesTopology = (outDegreeMap[node.id] || 0) === 0;
      } else if (topologyFilter === 'hub') {
        matchesTopology = (inDegreeMap[node.id] || 0) >= 3;
      }

      return matchesSearch && matchesType && matchesTopology;
    });
  }, [graph.nodes, searchQuery, typeFilter, topologyFilter, inDegreeMap, outDegreeMap]);

  // Helper to determine if a node belongs to Workspace Briefs or External/Target section
  const isBriefNode = (node) => {
    if (node.isTargetPage || node.isExternal || node.type === 'external') return false;
    if (node.briefId) {
      return (briefs || []).some((b) => b.id === node.briefId);
    }
    const matchesBrief = (briefs || []).some((b) => {
      const slug = (
        b.targetSlug ||
        `/blog/${(b.title || 'untitled')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')}`
      ).toLowerCase();
      const clean = slug.startsWith('/') ? slug : `/${slug}`;
      return (
        clean === node.url?.toLowerCase() ||
        (b.title && node.title && b.title.toLowerCase() === node.title.toLowerCase())
      );
    });
    if (matchesBrief) return true;
    if (!briefs || briefs.length === 0) {
      return (
        !node.isTargetPage &&
        (node.type === 'blog' || node.type === 'guide' || node.type === 'pillar')
      );
    }
    return false;
  };

  // Partition filtered nodes into Briefs and External/Target pages
  const { briefNodes, externalAndTargetNodes } = useMemo(() => {
    const bList = [];
    const tList = [];
    filteredNodes.forEach((node) => {
      if (isBriefNode(node)) {
        bList.push(node);
      } else {
        tList.push(node);
      }
    });
    return { briefNodes: bList, externalAndTargetNodes: tList };
  }, [filteredNodes, briefs]);

  // Map of nodeId -> array of { node, anchor } targets linked FROM this node
  const outgoingTargetsMap = useMemo(() => {
    const nodeById = new Map();
    (graph.nodes || []).forEach((n) => nodeById.set(n.id, n));
    const map = {};
    (graph.nodes || []).forEach((n) => {
      map[n.id] = [];
    });
    (graph.edges || []).forEach((e) => {
      const tgt = nodeById.get(e.target);
      if (tgt && map[e.source]) {
        map[e.source].push({ node: tgt, anchor: e.anchor });
      }
    });
    return map;
  }, [graph.nodes, graph.edges]);

  // Map of nodeId -> array of { node, anchor } sources linking TO this node
  const incomingSourcesMap = useMemo(() => {
    const nodeById = new Map();
    (graph.nodes || []).forEach((n) => nodeById.set(n.id, n));
    const map = {};
    (graph.nodes || []).forEach((n) => {
      map[n.id] = [];
    });
    (graph.edges || []).forEach((e) => {
      const src = nodeById.get(e.source);
      if (src && map[e.target]) {
        map[e.target].push({ node: src, anchor: e.anchor });
      }
    });
    return map;
  }, [graph.nodes, graph.edges]);

  // Recommendations for display (either selected node or whole site)
  const displayedRecommendations = useMemo(() => {
    const list = graph.recommendedLinks || [];
    if (recScope === 'all' || !selectedNode) {
      return list;
    }
    return list.filter((rec) => {
      return (
        rec.sourceId === selectedNode.id ||
        rec.targetId === selectedNode.id ||
        rec.sourceNode === selectedNode.url ||
        rec.targetNode === selectedNode.url
      );
    });
  }, [graph.recommendedLinks, recScope, selectedNode]);

  // Calculate Link Health Score (0 - 100)
  const nodeHealthScore = useMemo(() => {
    if (!selectedNode) return 0;
    let score = selectedNode.authority || 75;
    if (nodeIncomingEdges.length === 0) {
      score -= 10;
    } else {
      score += Math.min(10, nodeIncomingEdges.length * 3);
    }
    if (nodeOutgoingEdges.length === 0) {
      score -= 8;
    } else {
      score += Math.min(8, nodeOutgoingEdges.length * 2);
    }
    const hasLoop = (detectedLoops || []).some(
      (l) =>
        l.source === selectedNode.id || l.nodeA === selectedNode.id || l.nodeB === selectedNode.id
    );
    if (hasLoop) {
      score -= 20;
    }
    return Math.min(100, Math.max(10, Math.round(score)));
  }, [selectedNode, nodeIncomingEdges.length, nodeOutgoingEdges.length, detectedLoops]);

  const nodeHasLoop = useMemo(() => {
    if (!selectedNode) return false;
    return (detectedLoops || []).some(
      (l) =>
        l.source === selectedNode.id || l.nodeA === selectedNode.id || l.nodeB === selectedNode.id
    );
  }, [selectedNode, detectedLoops]);

  const getNodeTypeLabel = (node) => {
    if (!node) return 'PAGE';
    if (node.isExternal || node.type === 'external') return 'EXTERNAL RESOURCE';
    if (node.type === 'blog') return 'BLOG POST';
    if (node.type === 'guide') return 'GUIDE';
    if (node.type === 'product') return 'PRODUCT';
    if (node.type === 'commercial') return 'COMMERCIAL';
    return (node.type || 'PAGE').toUpperCase();
  };

  const getRecommendationSnippet = (rec, sourceNode, targetNode) => {
    if (rec.contextSnippet) return rec.contextSnippet;
    if (rec.snippet) return rec.snippet;

    const anchor = (rec.suggestedAnchor || targetNode?.title || 'content').trim();
    const lowerAnchor = anchor.toLowerCase();

    if (
      rec.reason &&
      rec.reason.toLowerCase().includes(lowerAnchor) &&
      !rec.reason.toLowerCase().includes('synergy') &&
      !rec.reason.toLowerCase().includes('connection on')
    ) {
      return rec.reason;
    }

    if (lowerAnchor.includes('cold outreach') || lowerAnchor.includes('outreach template')) {
      return `Pair your task list with proven ${anchor} to keep every follow-up organized.`;
    }
    if (lowerAnchor.includes('content workflow') || lowerAnchor.includes('workflow')) {
      return `Use the template to ${anchor} from first draft through publication.`;
    }
    if (lowerAnchor.includes('deliverability') || lowerAnchor.includes('email deliverability')) {
      return `Ensure reliable inbox placement and sender reputation using established ${anchor} practices.`;
    }
    if (lowerAnchor.includes('lead enrichment') || lowerAnchor.includes('enrichment')) {
      return `Scale outbound velocity and buyer intent with verified ${anchor}.`;
    }
    if (lowerAnchor.includes('aieo') || lowerAnchor.includes('answer engine')) {
      return `Optimize content for AI engine citation visibility with our proven ${anchor}.`;
    }

    return `Pair your task list with proven ${anchor} to keep every follow-up organized.`;
  };

  const renderSnippetWithHighlight = (snippetText, anchor) => {
    if (!snippetText) return null;
    if (!anchor) return snippetText;

    const lowerText = snippetText.toLowerCase();
    const lowerAnchor = anchor.toLowerCase().trim();
    const idx = lowerText.indexOf(lowerAnchor);

    if (idx === -1) {
      return (
        <>
          {snippetText} <mark className="seo-suggested-anchor-highlight">{anchor}</mark>
        </>
      );
    }

    const before = snippetText.substring(0, idx);
    const matched = snippetText.substring(idx, idx + anchor.length);
    const after = snippetText.substring(idx + anchor.length);

    return (
      <>
        {before}
        <mark className="seo-suggested-anchor-highlight">{matched}</mark>
        {after}
      </>
    );
  };

  // --------------------------------------------------------------------------
  // ACTIONS: Node CRUD
  // --------------------------------------------------------------------------

  // Add Page Node
  const handleAddNode = (e) => {
    if (e) e.preventDefault();
    if (!newNodeTitle.trim() || !newNodeUrl.trim()) return;

    let cleanUrl = newNodeUrl.trim();
    const isExt = /^https?:\/\//i.test(cleanUrl) || cleanUrl.startsWith('www.');
    if (!cleanUrl.startsWith('/') && !isExt) {
      cleanUrl = `/${cleanUrl}`;
    }

    const matchedCluster = (topicClusters || []).find(
      (c) =>
        c.topic === newNodeTopic.trim() ||
        (c.topic && newNodeTopic && c.topic.toLowerCase() === newNodeTopic.trim().toLowerCase())
    );

    const newNodeId = `node-${Date.now()}`;
    const isTarget =
      isExt ||
      newNodeType === 'external' ||
      newNodeType === 'commercial' ||
      newNodeType === 'product';
    const newNode = {
      id: newNodeId,
      title: newNodeTitle.trim(),
      url: cleanUrl,
      type: newNodeType,
      authority: Math.min(100, Math.max(1, Number(newNodeAuthority) || 75)),
      isTargetPage: isTarget,
      isExternal: isExt || newNodeType === 'external',
      topic: newNodeTopic.trim() || matchedCluster?.topic || undefined,
      topicClusterId: matchedCluster?.id || undefined,
      entities: matchedCluster?.entities || [],
    };

    const updatedGraph = {
      ...graph,
      nodes: [...graph.nodes, newNode],
    };

    updateGraph(updatedGraph);
    setSelectedNodeId(newNodeId);
    setIsAddNodeOpen(false);
    setNewNodeTitle('');
    setNewNodeUrl('');
    setNewNodeType('blog');
    setNewNodeAuthority(75);
    setNewNodeTopic('');
    setNotification(`Page "${newNode.title}" successfully added to Site Graph.`);
  };

  // Edit Page Node
  const handleSaveEditNode = (e) => {
    if (e) e.preventDefault();
    if (!selectedNode || !editNodeTitle.trim() || !editNodeUrl.trim()) return;

    let cleanUrl = editNodeUrl.trim();
    const isExt = /^https?:\/\//i.test(cleanUrl) || cleanUrl.startsWith('www.');
    if (!cleanUrl.startsWith('/') && !isExt) cleanUrl = `/${cleanUrl}`;

    const matchedCluster = (topicClusters || []).find(
      (c) =>
        c.topic === editNodeTopic.trim() ||
        (c.topic && editNodeTopic && c.topic.toLowerCase() === editNodeTopic.trim().toLowerCase())
    );

    const isTarget =
      isExt ||
      editNodeType === 'external' ||
      editNodeType === 'commercial' ||
      editNodeType === 'product';
    const updatedNodes = graph.nodes.map((n) =>
      n.id === selectedNode.id
        ? {
            ...n,
            title: editNodeTitle.trim(),
            url: cleanUrl,
            type: editNodeType,
            authority: Math.min(100, Math.max(1, Number(editNodeAuthority) || 75)),
            isTargetPage: isTarget !== undefined ? isTarget : n.isTargetPage,
            isExternal: isExt || editNodeType === 'external',
            topic: editNodeTopic.trim() || undefined,
            topicClusterId: matchedCluster?.id || n.topicClusterId,
            entities: matchedCluster?.entities || n.entities || [],
          }
        : n
    );

    const updatedGraph = { ...graph, nodes: updatedNodes };
    updateGraph(updatedGraph);
    setIsEditNodeOpen(false);
    setNotification(`Page "${editNodeTitle}" updated successfully.`);
  };

  // Delete Page Node (with cascading edge removal)
  const handleDeleteNode = (nodeToDelete) => {
    if (!nodeToDelete) return;
    const confirmDelete = window.confirm(
      `Are you sure you want to remove "${nodeToDelete.title}" from the site content graph? All connected inbound and outbound internal links will also be removed.`
    );
    if (!confirmDelete) return;

    const remainingNodes = graph.nodes.filter((n) => n.id !== nodeToDelete.id);
    const remainingEdges = graph.edges.filter(
      (e) => e.source !== nodeToDelete.id && e.target !== nodeToDelete.id
    );
    const remainingRecs = (graph.recommendedLinks || []).filter(
      (r) =>
        r.sourceId !== nodeToDelete.id &&
        r.targetId !== nodeToDelete.id &&
        r.sourceNode !== nodeToDelete.url &&
        r.targetNode !== nodeToDelete.url
    );

    const updatedGraph = {
      ...graph,
      nodes: remainingNodes,
      edges: remainingEdges,
      recommendedLinks: remainingRecs,
    };

    updateGraph(updatedGraph);
    setSelectedNodeId(remainingNodes[0]?.id || null);
    setNotification(`Page "${nodeToDelete.title}" and connected links removed.`);
  };

  // Sync all workspace briefs into Graph
  const handleSyncBriefs = () => {
    if (!Array.isArray(briefs) || briefs.length === 0) return;
    const synced = syncGraphWithBriefs(graph, briefs, topicClusters);
    updateGraph(synced);
    setNotification(
      'Successfully synced workspace briefs and target links into Site Content Graph.'
    );
  };

  // --------------------------------------------------------------------------
  // ACTIONS: Internal Link (Edge) CRUD
  // --------------------------------------------------------------------------

  // Add Internal Link
  const handleAddLink = (e) => {
    if (e) e.preventDefault();
    if (!selectedNode || !newLinkTargetId || !newLinkAnchor.trim()) return;

    if (newLinkTargetId === selectedNode.id) {
      alert('A page cannot link to itself.');
      return;
    }

    // Check duplicate
    const exists = graph.edges.some(
      (edge) => edge.source === selectedNode.id && edge.target === newLinkTargetId
    );
    if (exists) {
      alert('This internal link already exists in the graph.');
      return;
    }

    const newEdge = {
      source: selectedNode.id,
      target: newLinkTargetId,
      anchor: newLinkAnchor.trim(),
    };

    const targetNodeObj = graph.nodes.find((n) => n.id === newLinkTargetId);

    // If an existing recommendation matches this pair, clean it up
    const remainingRecs = (graph.recommendedLinks || []).filter(
      (r) =>
        !(
          (r.sourceId === selectedNode.id && r.targetId === newLinkTargetId) ||
          (r.sourceNode === selectedNode.url && r.targetNode === targetNodeObj?.url)
        )
    );

    const updatedGraph = {
      ...graph,
      edges: [...graph.edges, newEdge],
      recommendedLinks: remainingRecs,
    };

    updateGraph(updatedGraph);
    setIsAddLinkOpen(false);
    setNewLinkTargetId('');
    setNewLinkAnchor('');
    setNotification(`Internal link created to "${targetNodeObj?.title || 'target page'}".`);
  };

  // Delete Internal Link
  const handleDeleteEdge = (edgeToDelete) => {
    const updatedEdges = graph.edges.filter(
      (e) =>
        !(
          e.source === edgeToDelete.source &&
          e.target === edgeToDelete.target &&
          e.anchor === edgeToDelete.anchor
        )
    );

    const updatedGraph = { ...graph, edges: updatedEdges };
    updateGraph(updatedGraph);
    setNotification('Internal link removed from site graph.');
  };

  // --------------------------------------------------------------------------
  // ACTIONS: AI Internal Link Recommendations
  // --------------------------------------------------------------------------

  // Helper to determine topical synergy in client fallback
  const checkClientTopicalSynergy = (src, tgt) => {
    if (!src || !tgt || src.id === tgt.id) return { compatible: false, score: 0, reason: '' };

    const srcCluster = src.topicClusterId || src.clusterId;
    const tgtCluster = tgt.topicClusterId || tgt.clusterId;
    const sameCluster = Boolean(srcCluster && tgtCluster && srcCluster === tgtCluster);

    const srcTopic = (src.topic || '').trim().toLowerCase();
    const tgtTopic = (tgt.topic || '').trim().toLowerCase();
    const sameTopic = Boolean(srcTopic && tgtTopic && srcTopic === tgtTopic);

    // Entity overlap
    const srcEntities = (Array.isArray(src.entities) ? src.entities : [])
      .map((e) => String(e).toLowerCase().trim())
      .filter(Boolean);
    const tgtEntities = (Array.isArray(tgt.entities) ? tgt.entities : [])
      .map((e) => String(e).toLowerCase().trim())
      .filter(Boolean);
    const commonEntities = srcEntities.filter((e) => tgtEntities.includes(e));

    // Keyword overlap
    const STOP_WORDS = new Set([
      'guide',
      'blog',
      'with',
      'about',
      'this',
      'that',
      'from',
      'your',
      'page',
      'post',
      'article',
      'what',
      'when',
      'where',
      'which',
      'their',
      'there',
      'have',
      'more',
      'best',
      'overview',
      'introduction',
      'checklist',
      '2025',
      '2026',
      '2027',
      'features',
      'the',
      'and',
      'for',
    ]);
    const extractWords = (n) =>
      `${n.title || ''} ${n.url || ''} ${n.topic || ''}`
        .toLowerCase()
        .split(/[\s\-_/.,:;()]+/)
        .filter((w) => w.length > 3 && !STOP_WORDS.has(w) && !/^\d+$/.test(w));
    const srcWords = extractWords(src);
    const tgtWords = extractWords(tgt);
    const commonWords = srcWords.filter((w) => tgtWords.includes(w));

    const isCommercialTarget =
      tgt.type === 'commercial' ||
      tgt.type === 'product' ||
      (tgt.url && (tgt.url.includes('/pricing') || tgt.url.includes('/features/')));
    const B2B_THEMES = new Set([
      'lead',
      'enrichment',
      'sales',
      'email',
      'deliverability',
      'outreach',
      'cadence',
      'prospecting',
      'pipeline',
      'b2b',
      'crm',
      'automation',
      'fintech',
      'scoring',
      'aieo',
      'seo',
    ]);
    const srcHasB2B =
      srcWords.some((w) => B2B_THEMES.has(w)) ||
      (srcTopic && Array.from(B2B_THEMES).some((t) => srcTopic.includes(t)));
    const commercialSynergy = isCommercialTarget && srcHasB2B;

    if (sameCluster || sameTopic) {
      return {
        compatible: true,
        score: 92,
        reason: `Topical cluster alignment within "${src.topic || tgt.topic || 'shared cluster'}"`,
      };
    }
    if (commonEntities.length > 0) {
      return {
        compatible: true,
        score: 86,
        reason: `Shared semantic entities: ${commonEntities.slice(0, 2).join(', ')}`,
      };
    }
    if (commonWords.length >= 2) {
      return {
        compatible: true,
        score: 80,
        reason: `Contextual entity match on: "${Array.from(new Set(commonWords)).slice(0, 2).join(', ')}"`,
      };
    }
    if (commonWords.length === 1 && (src.type !== 'commercial' || tgt.type !== 'commercial')) {
      return {
        compatible: true,
        score: 74,
        reason: `Contextual theme connection on: "${commonWords[0]}"`,
      };
    }
    if (commercialSynergy) {
      return {
        compatible: true,
        score: 78,
        reason: `Funnel editorial readership to conversion landing page for ${tgt.title}`,
      };
    }

    return { compatible: false, score: 0, reason: '' };
  };

  // Generate / Refresh AI Recommendations
  const handleGenerateRecommendations = async () => {
    setIsGeneratingRecs(true);
    try {
      const response = await axiosInstance.post('/seo/graph/recommend', {
        nodes: graph.nodes,
        edges: graph.edges,
        focusNodeId: recScope === 'selected' ? selectedNode?.id || null : null,
        topicClusters,
      });

      if (response.data && response.data.success && response.data.data) {
        const { recommendations } = response.data.data;
        if (Array.isArray(recommendations) && recommendations.length > 0) {
          // Merge avoiding duplicates
          const existingRecKeys = new Set(
            (graph.recommendedLinks || []).map(
              (r) => `${r.sourceId || r.sourceNode}->${r.targetId || r.targetNode}`
            )
          );

          const freshRecs = recommendations.filter(
            (r) =>
              !existingRecKeys.has(`${r.sourceId || r.sourceNode}->${r.targetId || r.targetNode}`)
          );

          const updatedRecs = [...freshRecs, ...(graph.recommendedLinks || [])];
          const updatedGraph = { ...graph, recommendedLinks: updatedRecs };
          updateGraph(updatedGraph);
          setNotification(`Generated ${freshRecs.length} new contextual link suggestions.`);
        } else {
          setNotification('Graph analysis complete: No new topically relevant link gaps found.');
        }
      }
    } catch (err) {
      console.warn(
        '[ContentGraphTab] Backend AI recommendation fallback to client analysis:',
        err.message
      );
      // Client-side heuristic recommendation generator
      generateClientRecommendations();
    } finally {
      setIsGeneratingRecs(false);
    }
  };

  // Client-side intelligent fallback recommendation generator
  const generateClientRecommendations = () => {
    const existingEdgeSet = new Set(graph.edges.map((e) => `${e.source}->${e.target}`));
    const newSuggestions = [];

    // Prioritize linking from high-authority or selectedNode to orphan pages
    const potentialSources =
      recScope === 'selected' && selectedNode
        ? [selectedNode]
        : selectedNode
          ? [
              selectedNode,
              ...graph.nodes.filter((n) => n.id !== selectedNode.id && (n.authority || 0) >= 80),
            ]
          : graph.nodes;

    potentialSources.forEach((src) => {
      graph.nodes.forEach((tgt) => {
        if (src.id === tgt.id) return;
        if (existingEdgeSet.has(`${src.id}->${tgt.id}`)) return;
        if ((outDegreeMap[src.id] || 0) >= 8) return;

        // Strict topical check: Discard unrelated pairs
        const topComp = checkClientTopicalSynergy(src, tgt);
        if (!topComp.compatible) return;

        // Check reciprocal loop
        const isReciprocal = existingEdgeSet.has(`${tgt.id}->${src.id}`);

        let score = topComp.score;
        let reason = topComp.reason;

        if ((inDegreeMap[tgt.id] || 0) === 0) {
          score += 8;
          reason += ' (Establishes crawl equity path for orphan topic page)';
        }

        const cleanAnchor = tgt.title
          .replace(/^(the|a|an)\s+/i, '')
          .replace(/\s+(in|for)\s+\d{4}$/i, '')
          .toLowerCase();

        newSuggestions.push({
          id: `rec-local-${src.id}-${tgt.id}-${Date.now()}`,
          sourceId: src.id,
          targetId: tgt.id,
          sourceNode: src.url,
          targetNode: tgt.url,
          sourceTitle: src.title,
          targetTitle: tgt.title,
          suggestedAnchor: cleanAnchor || tgt.title,
          relevanceScore: Math.min(96, score),
          reason,
          loopSafe: !isReciprocal,
        });
      });
    });

    newSuggestions.sort((a, b) => b.relevanceScore - a.relevanceScore);
    const topPicks = newSuggestions.slice(0, 4);

    const updatedGraph = {
      ...graph,
      recommendedLinks: [...topPicks, ...(graph.recommendedLinks || [])],
    };

    updateGraph(updatedGraph);
    setNotification(
      topPicks.length > 0
        ? `Synthesized ${topPicks.length} contextually verified internal link recommendations.`
        : 'Analysis complete: No topically related link gaps found.'
    );
  };

  // Apply a Recommendation with One Click! (Instantly adds edge)
  const handleApplyRecommendation = (rec) => {
    // Resolve source & target node IDs
    let sourceId = rec.sourceId;
    let targetId = rec.targetId;

    if (!sourceId && rec.sourceNode) {
      const matched = graph.nodes.find((n) => n.url === rec.sourceNode);
      if (matched) sourceId = matched.id;
    }
    if (!targetId && rec.targetNode) {
      const matched = graph.nodes.find((n) => n.url === rec.targetNode);
      if (matched) targetId = matched.id;
    }

    if (!sourceId || !targetId) {
      alert('Could not resolve node references for this recommendation.');
      return;
    }

    const newEdge = {
      source: sourceId,
      target: targetId,
      anchor: rec.suggestedAnchor,
    };

    const remainingRecs = (graph.recommendedLinks || []).filter((r) => r.id !== rec.id);

    const updatedGraph = {
      ...graph,
      edges: [...graph.edges, newEdge],
      recommendedLinks: remainingRecs,
    };

    updateGraph(updatedGraph);
    setNotification(
      `Applied internal link: "${rec.sourceTitle || rec.sourceNode}" → "${rec.targetTitle || rec.targetNode}" with anchor "${rec.suggestedAnchor}".`
    );
  };

  // Dismiss a Recommendation
  const handleDismissRecommendation = (recId) => {
    const remainingRecs = (graph.recommendedLinks || []).filter((r) => r.id !== recId);
    const updatedGraph = { ...graph, recommendedLinks: remainingRecs };
    updateGraph(updatedGraph);
  };

  // Clear site graph
  const handleClearGraph = () => {
    const confirmClear = window.confirm(
      'Clear the Site Content Graph? This will remove all pages, links, and recommendations from your workspace.'
    );
    if (!confirmClear) return;
    updateGraph(EMPTY_CONTENT_GRAPH);
    setSelectedNodeId(null);
    try {
      localStorage.removeItem('lead_huntrix_seo_content_graph');
    } catch (e) {
      console.warn('Failed to clear graph from localStorage', e);
    }
    setNotification('Content graph cleared.');
  };

  // Export Graph JSON
  const handleExportGraph = () => {
    const dataStr =
      'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(graph, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', `content_graph_${Date.now()}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  // --------------------------------------------------------------------------
  // SVG Canvas Coordinates Calculation for Visual Topology
  // --------------------------------------------------------------------------
  const nodePositions = useMemo(() => {
    const positions = {};
    const total = graph.nodes.length;
    if (total === 0) return positions;

    const width = 800;
    const height = 440;
    const centerX = width / 2;
    const centerY = height / 2;
    const radiusX = 300;
    const radiusY = 160;

    graph.nodes.forEach((n, idx) => {
      const angle = (idx / total) * 2 * Math.PI - Math.PI / 2;
      positions[n.id] = {
        x: Math.round(centerX + radiusX * Math.cos(angle)),
        y: Math.round(centerY + radiusY * Math.sin(angle)),
      };
    });

    return positions;
  }, [graph.nodes]);

  return (
    <div className="seo-tab-container" data-testid="content-graph-tab">
      {/* HEADER ROW WITH SPEC TAG & DYNAMIC ACTION BUTTONS */}
      <div className="seo-tab-header-row">
        <div>
          <h2 className="seo-section-title">
            <i className="fas fa-project-diagram"></i> Site Content Graph &amp; Internal Linking
          </h2>
          <p className="seo-section-desc">
            Analyze workspace site link topology, recommend contextually relevant anchor text, and
            prevent orphan pages and circular link loops.
          </p>
        </div>

        <div className="seo-tab-actions">
          {/* Quick sync from briefs button */}
          {unsyncedBriefs.length > 0 && (
            <button
              type="button"
              className="seo-btn seo-btn-secondary"
              onClick={handleSyncBriefs}
              title="Import active content briefs as indexed pages"
              data-testid="sync-briefs-btn"
            >
              <i className="fas fa-arrows-rotate text-purple"></i>
              <span>Sync Briefs ({unsyncedBriefs.length})</span>
            </button>
          )}

          {/* Add Page Node */}
          <button
            type="button"
            className="seo-btn seo-btn-primary"
            onClick={() => setIsAddNodeOpen(true)}
            data-testid="add-page-btn"
          >
            <i className="fas fa-plus"></i>
            <span>Add Page</span>
          </button>

          {/* Reset / Export dropdown */}
          <button
            type="button"
            className="seo-btn seo-btn-secondary"
            onClick={handleExportGraph}
            title="Export Graph JSON"
          >
            <i className="fas fa-download"></i>
          </button>
          {graph.nodes.length > 0 && (
            <button
              type="button"
              className="seo-btn seo-btn-secondary"
              onClick={handleClearGraph}
              title="Clear all pages and links"
              data-testid="clear-graph-btn"
            >
              <i className="fas fa-trash-can"></i>
            </button>
          )}
        </div>
      </div>

      {/* GRAPH HEALTH & METRICS STRIP */}
      <div className="seo-graph-kpi-bar">
        <div className="seo-graph-kpi-card">
          <span className="seo-graph-kpi-num">{graph.nodes.length}</span>
          <span className="seo-graph-kpi-lbl">
            <i className="fas fa-circle-nodes text-blue"></i> Pages Indexed
          </span>
        </div>

        <div className="seo-graph-kpi-card">
          <span className="seo-graph-kpi-num">{graph.edges.length}</span>
          <span className="seo-graph-kpi-lbl">
            <i className="fas fa-arrows-split-up-and-left text-green"></i> Internal Edges
          </span>
        </div>

        <div
          className={`seo-graph-kpi-card clickable ${orphanNodes.length > 0 ? 'warning' : ''}`}
          onClick={() => setTopologyFilter(topologyFilter === 'orphan' ? 'all' : 'orphan')}
          title="Click to filter orphan pages"
          data-testid="orphan-kpi-card"
        >
          <span className="seo-graph-kpi-num">{orphanNodes.length}</span>
          <span className="seo-graph-kpi-lbl">
            <i className="fas fa-triangle-exclamation"></i> Orphan Pages
          </span>
        </div>

        <div
          className={`seo-graph-kpi-card ${detectedLoops.length > 0 ? 'danger' : 'safe'}`}
          title={
            detectedLoops.length > 0
              ? detectedLoops.map((l) => l.description).join('\n')
              : 'Site graph is 100% loop-free and compliant with FR-M11-007'
          }
        >
          <span className="seo-graph-kpi-num">{detectedLoops.length}</span>
          <span className="seo-graph-kpi-lbl">
            <i className={detectedLoops.length > 0 ? 'fas fa-repeat' : 'fas fa-shield-check'}></i>{' '}
            Circular Loops
          </span>
        </div>

        {/* View Mode Toggle */}
        <div className="seo-mode-toggle-pills" style={{ marginLeft: 'auto' }}>
          <button
            type="button"
            className={`seo-mode-pill ${viewMode === 'split' ? 'active' : ''}`}
            onClick={() => setViewMode('split')}
            data-testid="view-split-btn"
          >
            <i className="fas fa-table-columns"></i>
            <span>Cards &amp; Detail</span>
          </button>
          <button
            type="button"
            className={`seo-mode-pill ${viewMode === 'canvas' ? 'active' : ''}`}
            onClick={() => setViewMode('canvas')}
            data-testid="view-canvas-btn"
          >
            <i className="fas fa-circle-nodes"></i>
            <span>Visual Topology</span>
          </button>
        </div>
      </div>

      {/* NOTIFICATION TOAST */}
      {notification && (
        <div className="seo-notification-banner" data-testid="graph-notification">
          <i className="fas fa-check-circle text-green"></i>
          <span>{notification}</span>
          <button type="button" className="seo-banner-close" onClick={() => setNotification(null)}>
            &times;
          </button>
        </div>
      )}

      {/* ORPHAN ALERT BANNER IF ORPHANS EXIST */}
      {orphanNodes.length > 0 && topologyFilter !== 'orphan' && (
        <div className="seo-orphan-alert-row">
          <i className="fas fa-triangle-exclamation text-amber"></i>
          <span>
            <strong>{orphanNodes.length} Orphan Pages Detected:</strong> Search crawlers cannot find
            these pages via internal links.
          </span>
          <button
            type="button"
            className="seo-filter-mini-btn"
            onClick={() => setTopologyFilter('orphan')}
          >
            Filter Orphans ({orphanNodes.length})
          </button>
        </div>
      )}

      {/* CIRCULAR LOOP WARNING BANNER */}
      {detectedLoops.length > 0 && (
        <div className="seo-loop-alert-row" data-testid="circular-loop-banner">
          <i className="fas fa-repeat text-red"></i>
          <span>
            <strong>{detectedLoops.length} Circular Loops Detected:</strong> Reciprocal 2-way links
            can create crawl traps and dilute link equity.
          </span>
        </div>
      )}

      {/* ==================================================================== */}
      {/* EMPTY STATE: WHEN NO NODES ARE IN THE GRAPH                          */}
      {/* ==================================================================== */}
      {graph.nodes.length === 0 ? (
        <div
          className="seo-empty-nodes-state"
          data-testid="empty-graph-state"
          style={{
            padding: '60px 20px',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            textAlign: 'center',
            margin: '20px 0',
          }}
        >
          <i
            className="fas fa-project-diagram"
            style={{ fontSize: '48px', color: '#94a3b8', marginBottom: '16px' }}
          ></i>
          <h3
            style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px 0' }}
          >
            No Pages in Site Content Graph
          </h3>
          <p
            style={{
              fontSize: '14px',
              color: '#64748b',
              maxWidth: '520px',
              margin: '0 auto 24px auto',
              lineHeight: '1.5',
            }}
          >
            Your content graph is currently empty. Add your published pages or import active content
            briefs to map your site internal link architecture, resolve orphan pages, and generate
            contextual anchor text.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              type="button"
              className="seo-btn seo-btn-primary"
              onClick={() => setIsAddNodeOpen(true)}
              data-testid="empty-add-page-btn"
            >
              <i className="fas fa-plus"></i> Add First Page
            </button>
            {unsyncedBriefs.length > 0 && (
              <button
                type="button"
                className="seo-btn seo-btn-secondary"
                onClick={handleSyncBriefs}
                data-testid="empty-sync-briefs-btn"
              >
                <i className="fas fa-arrows-rotate text-purple"></i> Sync Briefs (
                {unsyncedBriefs.length})
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* ==================================================================== */}
          {/* MODE 1: VISUAL TOPOLOGY CANVAS (INTERACTIVE SVG MAP)                 */}
          {/* ==================================================================== */}
          {viewMode === 'canvas' && (
            <div className="seo-canvas-view-wrap" data-testid="graph-canvas-container">
              <div className="seo-canvas-toolbar">
                <span className="seo-canvas-title">
                  <i className="fas fa-diagram-project text-purple"></i> Interactive Topology Map
                </span>
                <div className="seo-canvas-legend">
                  <span className="seo-legend-item">
                    <span className="seo-legend-dot blog"></span> Blog
                  </span>
                  <span className="seo-legend-item">
                    <span className="seo-legend-dot product"></span> Product
                  </span>
                  <span className="seo-legend-item">
                    <span className="seo-legend-dot guide"></span> Guide
                  </span>
                  <span className="seo-legend-item">
                    <span className="seo-legend-dot commercial"></span> Commercial
                  </span>
                  <span className="seo-legend-item">
                    <span className="seo-legend-dot external"></span> External Resource
                  </span>
                  <span className="seo-legend-item">
                    <span className="seo-legend-line green"></span> Inbound
                  </span>
                  <span className="seo-legend-item">
                    <span className="seo-legend-line blue"></span> Outbound
                  </span>
                </div>
              </div>

              <div className="seo-svg-canvas-container">
                <svg
                  viewBox="0 0 800 440"
                  className="seo-topology-svg"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <marker
                      id="arrow-default"
                      viewBox="0 0 10 10"
                      refX="24"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M 0 1 L 9 5 L 0 9 z" fill="#94a3b8" />
                    </marker>
                    <marker
                      id="arrow-outbound"
                      viewBox="0 0 10 10"
                      refX="24"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M 0 1 L 9 5 L 0 9 z" fill="#2563eb" />
                    </marker>
                    <marker
                      id="arrow-inbound"
                      viewBox="0 0 10 10"
                      refX="24"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M 0 1 L 9 5 L 0 9 z" fill="#10b981" />
                    </marker>
                  </defs>

                  {/* DRAW EDGES (DIRECTED ARROWS) */}
                  {graph.edges.map((edge, idx) => {
                    const srcPos = nodePositions[edge.source];
                    const tgtPos = nodePositions[edge.target];
                    if (!srcPos || !tgtPos) return null;

                    const isOutgoingFromSelected = selectedNode && edge.source === selectedNode.id;
                    const isIncomingToSelected = selectedNode && edge.target === selectedNode.id;

                    let strokeColor = '#cbd5e1';
                    let strokeWidth = 1.5;
                    let markerEnd = 'url(#arrow-default)';

                    if (isOutgoingFromSelected) {
                      strokeColor = '#2563eb';
                      strokeWidth = 2.5;
                      markerEnd = 'url(#arrow-outbound)';
                    } else if (isIncomingToSelected) {
                      strokeColor = '#10b981';
                      strokeWidth = 2.5;
                      markerEnd = 'url(#arrow-inbound)';
                    }

                    // Curved cubic Bezier path
                    const dx = tgtPos.x - srcPos.x;
                    const dy = tgtPos.y - srcPos.y;
                    const cx1 = srcPos.x + dx * 0.25 - dy * 0.15;
                    const cy1 = srcPos.y + dy * 0.25 + dx * 0.15;
                    const cx2 = srcPos.x + dx * 0.75 - dy * 0.15;
                    const cy2 = srcPos.y + dy * 0.75 + dx * 0.15;

                    return (
                      <g key={`edge-${idx}`} className="seo-svg-edge-group">
                        <path
                          d={`M ${srcPos.x} ${srcPos.y} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${tgtPos.x} ${tgtPos.y}`}
                          fill="none"
                          stroke={strokeColor}
                          strokeWidth={strokeWidth}
                          markerEnd={markerEnd}
                          strokeDasharray={
                            isOutgoingFromSelected || isIncomingToSelected ? 'none' : '3 3'
                          }
                        />
                      </g>
                    );
                  })}

                  {/* DRAW NODES */}
                  {graph.nodes.map((node) => {
                    const pos = nodePositions[node.id];
                    if (!pos) return null;
                    const isSelected = selectedNode?.id === node.id;
                    const inCount = inDegreeMap[node.id] || 0;
                    const isOrphan = inCount === 0;

                    let fillColor = '#ffffff';
                    let strokeColor = '#3b82f6';
                    if (node.type === 'product') strokeColor = '#10b981';
                    if (node.type === 'commercial') strokeColor = '#f59e0b';
                    if (node.type === 'guide') strokeColor = '#8b5cf6';
                    if (node.type === 'external' || node.isExternal) strokeColor = '#ec4899';

                    return (
                      <g
                        key={node.id}
                        className={`seo-svg-node-group ${isSelected ? 'selected' : ''}`}
                        transform={`translate(${pos.x}, ${pos.y})`}
                        onClick={() => setSelectedNodeId(node.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* Pulsing selection circle */}
                        {isSelected && (
                          <circle
                            className="seo-node-selection-ring"
                            cx="0"
                            cy="0"
                            r="26"
                            fill="none"
                            stroke="#2563eb"
                            strokeWidth="2"
                            strokeDasharray="3 3"
                            opacity="0.8"
                          />
                        )}

                        {/* Main node bubble */}
                        <circle
                          className="seo-node-bubble"
                          cx="0"
                          cy="0"
                          r="18"
                          fill={fillColor}
                          stroke={strokeColor}
                          strokeWidth={isSelected ? '3.5' : '2'}
                          filter="drop-shadow(0px 2px 4px rgba(0,0,0,0.1))"
                        />

                        {/* Authority badge text inside circle */}
                        <text
                          y="4"
                          textAnchor="middle"
                          fontSize="10"
                          fontWeight="bold"
                          fill="#1e293b"
                          style={{ pointerEvents: 'none' }}
                        >
                          {node.authority || 75}
                        </text>

                        {/* Node title label */}
                        <text
                          y="32"
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight={isSelected ? '700' : '500'}
                          fill={isSelected ? '#1d4ed8' : '#334155'}
                        >
                          {node.title.length > 22 ? `${node.title.slice(0, 20)}…` : node.title}
                        </text>

                        {/* Orphan warning badge */}
                        {isOrphan && (
                          <g transform="translate(10, -14)">
                            <circle r="6" fill="#ef4444" />
                            <text
                              y="3"
                              textAnchor="middle"
                              fontSize="8"
                              fill="#ffffff"
                              fontWeight="bold"
                            >
                              !
                            </text>
                          </g>
                        )}
                      </g>
                    );
                  })}
                </svg>
              </div>

              {/* Quick Inspector strip below SVG canvas */}
              {selectedNode && (
                <div className="seo-canvas-selected-bar">
                  <div>
                    <span className={`seo-node-type-pill ${selectedNode.type}`}>
                      {selectedNode.type}
                    </span>
                    <strong>{selectedNode.title}</strong>
                    <code>{selectedNode.url}</code>
                  </div>
                  <div className="seo-canvas-bar-actions">
                    <span className="seo-pill-tag">
                      Inbound: {inDegreeMap[selectedNode.id] || 0}
                    </span>
                    <span className="seo-pill-tag">
                      Outbound: {outDegreeMap[selectedNode.id] || 0}
                    </span>
                    <button
                      type="button"
                      className="seo-btn seo-btn-secondary"
                      onClick={() => setIsAddLinkOpen(true)}
                    >
                      <i className="fas fa-link"></i> Add Outgoing Link
                    </button>
                    <button
                      type="button"
                      className="seo-btn seo-btn-secondary"
                      onClick={() => setViewMode('split')}
                    >
                      <i className="fas fa-arrow-up-right-from-square"></i> Inspect in Detail
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ==================================================================== */}
          {/* MODE 2: SPLIT LAYOUT (NODE CARDS + DETAIL & AI RECOMMENDATIONS)      */}
          {/* ==================================================================== */}
          {viewMode === 'split' && (
            <div className="seo-split-layout">
              {/* LEFT: NODES TOPOLOGY TABLE / CARDS */}
              <div className="seo-split-list">
                {/* Filter and Search Bar */}
                <div className="seo-list-filter-bar">
                  <div className="seo-search-input-wrap">
                    <i className="fas fa-search seo-search-icon"></i>
                    <input
                      type="text"
                      className="seo-search-input"
                      placeholder="Filter pages by title or URL..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      data-testid="search-node-input"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        className="seo-search-clear-btn"
                        onClick={() => setSearchQuery('')}
                      >
                        &times;
                      </button>
                    )}
                  </div>

                  {/* Filter Pills */}
                  <div className="seo-mini-filter-pills">
                    <button
                      type="button"
                      className={`seo-mini-pill ${topologyFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setTopologyFilter('all')}
                    >
                      All ({graph.nodes.length})
                    </button>
                    <button
                      type="button"
                      className={`seo-mini-pill ${topologyFilter === 'orphan' ? 'active warning' : ''}`}
                      onClick={() => setTopologyFilter('orphan')}
                    >
                      Orphans ({orphanNodes.length})
                    </button>
                    <button
                      type="button"
                      className={`seo-mini-pill ${topologyFilter === 'hub' ? 'active' : ''}`}
                      onClick={() => setTopologyFilter('hub')}
                    >
                      Hubs
                    </button>
                    <button
                      type="button"
                      className={`seo-mini-pill ${topologyFilter === 'deadend' ? 'active' : ''}`}
                      onClick={() => setTopologyFilter('deadend')}
                    >
                      Dead Ends ({deadEndNodes.length})
                    </button>
                  </div>
                </div>

                <div className="seo-card-list" data-testid="node-card-list">
                  {filteredNodes.length > 0 ? (
                    <>
                      {/* SECTION 1: WORKSPACE CONTENT BRIEFS */}
                      <div className="seo-card-section" data-testid="briefs-section">
                        <div
                          className="seo-card-section-header"
                          onClick={() => setBriefsSectionCollapsed((prev) => !prev)}
                          title="Click to collapse / expand section"
                        >
                          <div className="seo-card-section-title-wrap">
                            <i className="fas fa-file-lines text-purple"></i>
                            <h4 className="seo-card-section-title">Workspace Content Briefs</h4>
                            <span className="seo-card-section-count purple">
                              {briefNodes.length}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="seo-card-section-toggle"
                            aria-label="Toggle section"
                          >
                            <i
                              className={`fas fa-chevron-${briefsSectionCollapsed ? 'down' : 'up'}`}
                            ></i>
                          </button>
                        </div>
                        <div className="seo-card-section-desc">
                          Authored articles and guides from Content Briefs Studio
                        </div>

                        {!briefsSectionCollapsed && (
                          <div className="seo-card-section-cards">
                            {briefNodes.length > 0 ? (
                              briefNodes.map((node) => {
                                const isSelected = selectedNode?.id === node.id;
                                const liveInbound = inDegreeMap[node.id] || 0;
                                const liveOutbound = outDegreeMap[node.id] || 0;
                                const isOrphan = liveInbound === 0;
                                const outboundConnections = outgoingTargetsMap[node.id] || [];
                                const isCitingSelectedTarget =
                                  selectedNode &&
                                  selectedNode.id !== node.id &&
                                  outboundConnections.some((c) => c.node.id === selectedNode.id);

                                return (
                                  <div
                                    key={node.id}
                                    className={`seo-graph-node-card ${isSelected ? 'selected' : ''} ${isCitingSelectedTarget ? 'highlighted-connection' : ''}`}
                                    onClick={() => setSelectedNodeId(node.id)}
                                    data-testid={`node-card-${node.id}`}
                                  >
                                    <div className="seo-node-card-top">
                                      <div
                                        style={{
                                          display: 'flex',
                                          gap: '6px',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <span className={`seo-node-type-pill ${node.type}`}>
                                          {node.type}
                                        </span>
                                        <span className="seo-brief-source-badge">
                                          <i className="fas fa-feather-alt"></i> Brief
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          display: 'flex',
                                          gap: '6px',
                                          alignItems: 'center',
                                        }}
                                      >
                                        {isCitingSelectedTarget && (
                                          <span
                                            className="seo-connected-indicator source"
                                            title="This brief connects to the selected target page"
                                          >
                                            <i className="fas fa-check-circle"></i> Cites Selected
                                            Target
                                          </span>
                                        )}
                                        {isOrphan && (
                                          <span
                                            className="seo-orphan-badge"
                                            title="No internal pages link here"
                                          >
                                            Orphan
                                          </span>
                                        )}
                                        <span className="seo-node-authority">
                                          Authority: {node.authority}/100
                                        </span>
                                      </div>
                                    </div>

                                    <h3 className="seo-node-title">{node.title}</h3>
                                    <code className="seo-node-url">{node.url}</code>

                                    {/* Outbound connection chips to external/targets */}
                                    <div className="seo-node-connections-panel">
                                      <div className="seo-node-connections-header">
                                        <i className="fas fa-arrow-up-right-from-square text-purple"></i>
                                        <span>Links to ({outboundConnections.length}):</span>
                                      </div>
                                      {outboundConnections.length > 0 ? (
                                        <div className="seo-connections-chips">
                                          {outboundConnections.map(({ node: target, anchor }) => {
                                            const isTargetExternal =
                                              target.isExternal ||
                                              target.type === 'external' ||
                                              /^https?:\/\//i.test(target.url);
                                            return (
                                              <button
                                                key={target.id}
                                                type="button"
                                                className={`seo-connection-chip ${isTargetExternal ? 'chip-external' : 'chip-target'} ${selectedNode?.id === target.id ? 'active' : ''}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setSelectedNodeId(target.id);
                                                }}
                                                title={`Anchor text: "${anchor || ''}" | View ${target.title}`}
                                              >
                                                <i
                                                  className={
                                                    isTargetExternal
                                                      ? 'fas fa-arrow-up-right-from-square'
                                                      : 'fas fa-bullseye'
                                                  }
                                                ></i>
                                                <span className="seo-chip-text">
                                                  {target.title}
                                                </span>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      ) : (
                                        <span className="seo-connections-none">
                                          No target pages or external citations linked
                                        </span>
                                      )}
                                    </div>

                                    <div className="seo-node-metrics-bar">
                                      <span title="Inbound Internal Links pointing here">
                                        <i className="fas fa-arrow-down-left text-green"></i>{' '}
                                        {liveInbound} Inbound
                                      </span>
                                      <span title="Outbound Internal Links from this page">
                                        <i className="fas fa-arrow-up-right text-blue"></i>{' '}
                                        {liveOutbound} Outbound
                                      </span>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="seo-empty-nodes-state" style={{ padding: '16px' }}>
                                <p style={{ margin: 0, fontSize: '12px' }}>
                                  No briefs match your filter criteria.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* SECTION 2: LINKED EXTERNAL WEBSITES & TARGET PAGES */}
                      <div className="seo-card-section" data-testid="targets-section">
                        <div
                          className="seo-card-section-header"
                          onClick={() => setTargetsSectionCollapsed((prev) => !prev)}
                          title="Click to collapse / expand section"
                        >
                          <div className="seo-card-section-title-wrap">
                            <i className="fas fa-arrow-up-right-from-square text-blue"></i>
                            <h4 className="seo-card-section-title">
                              Linked External &amp; Target Pages
                            </h4>
                            <span className="seo-card-section-count blue">
                              {externalAndTargetNodes.length}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="seo-card-section-toggle"
                            aria-label="Toggle section"
                          >
                            <i
                              className={`fas fa-chevron-${targetsSectionCollapsed ? 'down' : 'up'}`}
                            ></i>
                          </button>
                        </div>
                        <div className="seo-card-section-desc">
                          External citations and target landing pages linked by briefs
                        </div>

                        {!targetsSectionCollapsed && (
                          <div className="seo-card-section-cards">
                            {externalAndTargetNodes.length > 0 ? (
                              externalAndTargetNodes.map((node) => {
                                const isSelected = selectedNode?.id === node.id;
                                const liveInbound = inDegreeMap[node.id] || 0;
                                const liveOutbound = outDegreeMap[node.id] || 0;
                                const inboundConnections = incomingSourcesMap[node.id] || [];
                                const isTargetOfSelected =
                                  selectedNode &&
                                  selectedNode.id !== node.id &&
                                  inboundConnections.some((c) => c.node.id === selectedNode.id);
                                const isExternal =
                                  node.isExternal ||
                                  node.type === 'external' ||
                                  /^https?:\/\//i.test(node.url);

                                return (
                                  <div
                                    key={node.id}
                                    className={`seo-graph-node-card ${isSelected ? 'selected' : ''} ${isTargetOfSelected ? 'highlighted-connection' : ''}`}
                                    onClick={() => setSelectedNodeId(node.id)}
                                    data-testid={`node-card-${node.id}`}
                                  >
                                    <div className="seo-node-card-top">
                                      <span
                                        className={`seo-node-type-pill ${isExternal ? 'external' : node.type || 'target'}`}
                                      >
                                        {isExternal ? (
                                          <>
                                            <i className="fas fa-globe"></i> External Resource
                                          </>
                                        ) : (
                                          <>
                                            <i className="fas fa-bullseye"></i>{' '}
                                            {node.type || 'Target'}
                                          </>
                                        )}
                                      </span>
                                      <div
                                        style={{
                                          display: 'flex',
                                          gap: '6px',
                                          alignItems: 'center',
                                        }}
                                      >
                                        {isTargetOfSelected && (
                                          <span
                                            className="seo-connected-indicator target"
                                            title="Linked by the currently selected brief"
                                          >
                                            <i className="fas fa-link"></i> Linked by Selected Brief
                                          </span>
                                        )}
                                        <span className="seo-node-authority">
                                          Authority: {node.authority}/100
                                        </span>
                                      </div>
                                    </div>

                                    <h3 className="seo-node-title">{node.title}</h3>
                                    {isExternal ? (
                                      <a
                                        href={node.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="seo-node-url external-link"
                                        onClick={(e) => e.stopPropagation()}
                                        title="Open external link in new tab"
                                      >
                                        <span>{node.url}</span>
                                        <i className="fas fa-external-link-alt seo-ext-icon"></i>
                                      </a>
                                    ) : (
                                      <code className="seo-node-url">{node.url}</code>
                                    )}

                                    {/* Inbound source briefs chips */}
                                    <div className="seo-node-connections-panel">
                                      <div className="seo-node-connections-header">
                                        <i className="fas fa-file-import text-blue"></i>
                                        <span>
                                          Referenced by Briefs ({inboundConnections.length}):
                                        </span>
                                      </div>
                                      {inboundConnections.length > 0 ? (
                                        <div className="seo-connections-chips">
                                          {inboundConnections.map(
                                            ({ node: sourceBrief, anchor }) => (
                                              <button
                                                key={sourceBrief.id}
                                                type="button"
                                                className={`seo-connection-chip chip-source ${selectedNode?.id === sourceBrief.id ? 'active' : ''}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setSelectedNodeId(sourceBrief.id);
                                                }}
                                                title={`Referenced with anchor: "${anchor || ''}" | View Brief: ${sourceBrief.title}`}
                                              >
                                                <i className="fas fa-file-alt"></i>
                                                <span className="seo-chip-text">
                                                  {sourceBrief.title}
                                                </span>
                                              </button>
                                            )
                                          )}
                                        </div>
                                      ) : (
                                        <span className="seo-connections-none unlinked">
                                          <i className="fas fa-unlink"></i> Not referenced by any
                                          active brief
                                        </span>
                                      )}
                                    </div>

                                    <div className="seo-node-metrics-bar">
                                      <span title="Inbound Internal Links pointing here">
                                        <i className="fas fa-arrow-down-left text-green"></i>{' '}
                                        {liveInbound} Inbound
                                      </span>
                                      <span title="Outbound Internal Links from this page">
                                        <i className="fas fa-arrow-up-right text-blue"></i>{' '}
                                        {liveOutbound} Outbound
                                      </span>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="seo-empty-nodes-state" style={{ padding: '16px' }}>
                                <p style={{ margin: 0, fontSize: '12px' }}>
                                  No external or target pages match your filter criteria.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="seo-empty-nodes-state">
                      <i className="fas fa-file-circle-question"></i>
                      <p>No indexed pages match your filter criteria.</p>
                      <button
                        type="button"
                        className="seo-btn seo-btn-secondary"
                        onClick={() => {
                          setSearchQuery('');
                          setTopologyFilter('all');
                          setTypeFilter('all');
                        }}
                      >
                        Clear Filters
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT: LINK DETAILS & ALGORITHMIC RECOMMENDATIONS */}
              <div className="seo-split-detail">
                {selectedNode ? (
                  <div className="seo-detail-box">
                    {/* NODE HEADER & ACTION BUTTONS */}
                    <div className="seo-detail-header-v2">
                      <div className="seo-node-badge-row">
                        <span className="seo-node-type-badge">
                          {getNodeTypeLabel(selectedNode)}
                        </span>
                        {nodeIncomingEdges.length === 0 ? (
                          <span className="seo-orphan-status-indicator">
                            <span className="seo-status-dot-orange">●</span> Orphan page
                          </span>
                        ) : (
                          <span className="seo-connected-status-indicator">
                            <span className="seo-status-dot-green">●</span> Connected page
                          </span>
                        )}
                        {selectedNode.topic && (
                          <span
                            className="seo-node-topic-pill"
                            style={{
                              marginLeft: '6px',
                              fontSize: '0.75rem',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              background: '#e0e7ff',
                              color: '#3730a3',
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                            title={`Topic Cluster: ${selectedNode.topic}`}
                          >
                            <i className="fas fa-layer-group"></i>
                            {selectedNode.topic}
                          </span>
                        )}
                      </div>

                      <div className="seo-split-title-row">
                        <div>
                          <h3 className="seo-split-main-title">{selectedNode.title}</h3>
                          {selectedNode.isExternal ||
                          selectedNode.type === 'external' ||
                          /^https?:\/\//i.test(selectedNode.url) ? (
                            <a
                              href={selectedNode.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="seo-split-url external-link"
                              title="Open in new tab"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                              }}
                            >
                              <code>{selectedNode.url}</code>
                              <i
                                className="fas fa-external-link-alt"
                                style={{ fontSize: '11px' }}
                              ></i>
                            </a>
                          ) : (
                            <span className="seo-split-url">{selectedNode.url}</span>
                          )}
                        </div>

                        <div className="seo-split-header-actions">
                          <button
                            type="button"
                            className="seo-btn-edit-details"
                            onClick={() => setIsEditNodeOpen(true)}
                            title="Edit details"
                            data-testid="edit-page-btn"
                          >
                            Edit details
                          </button>
                          <button
                            type="button"
                            className="seo-btn-delete-node-subtle"
                            onClick={() => handleDeleteNode(selectedNode)}
                            title="Remove page from site graph"
                            data-testid="delete-page-btn"
                          >
                            <i className="fas fa-trash-can"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* THREE OVERVIEW STAT CARDS */}
                    <div className="seo-overview-cards-row">
                      {/* CARD 1: INBOUND LINKS */}
                      <div className="seo-overview-card">
                        <div className="seo-overview-card-header">
                          <span className="seo-overview-card-label">INBOUND LINKS</span>
                          <span className="seo-overview-card-icon peach">
                            <i className="fas fa-arrow-up"></i>
                          </span>
                        </div>
                        <div className="seo-overview-card-stat">{nodeIncomingEdges.length}</div>
                        <div className="seo-overview-card-subtext">
                          {nodeIncomingEdges.length === 0
                            ? 'No pages point here yet'
                            : nodeIncomingEdges.length === 1
                              ? '1 link pointing here'
                              : `${nodeIncomingEdges.length} links pointing here`}
                        </div>
                      </div>

                      {/* CARD 2: OUTBOUND LINKS */}
                      <div className="seo-overview-card">
                        <div className="seo-overview-card-header">
                          <span className="seo-overview-card-label">OUTBOUND LINKS</span>
                          <span className="seo-overview-card-icon blue">
                            <i className="fas fa-arrow-down"></i>
                          </span>
                        </div>
                        <div className="seo-overview-card-stat">{nodeOutgoingEdges.length}</div>
                        <div className="seo-overview-card-subtext">
                          {nodeOutgoingEdges.length === 1
                            ? 'Link from this page'
                            : nodeOutgoingEdges.length === 0
                              ? 'No links from this page'
                              : `${nodeOutgoingEdges.length} links from this page`}
                        </div>
                      </div>

                      {/* CARD 3: LINK HEALTH */}
                      <div className="seo-overview-card">
                        <div className="seo-overview-card-header">
                          <span className="seo-overview-card-label">LINK HEALTH</span>
                          <span className="seo-overview-card-icon green">
                            <i className="fas fa-check"></i>
                          </span>
                        </div>
                        <div className="seo-overview-card-stat">
                          {nodeHealthScore}
                          <span className="seo-overview-card-denom">/100</span>
                        </div>
                        <div className="seo-overview-card-subtext">
                          {nodeHasLoop ? 'Circular loops detected' : 'No circular loops detected'}
                        </div>
                      </div>
                    </div>

                    {/* AI LINK SUGGESTIONS SECTION */}
                    <div className="seo-ai-suggestions-header">
                      <div className="seo-ai-suggestions-header-left">
                        <div className="seo-ai-suggestions-icon-box">
                          <i className="fas fa-wand-magic-sparkles"></i>
                        </div>
                        <div>
                          <h3 className="seo-ai-suggestions-title">AI link suggestions</h3>
                          <p className="seo-ai-suggestions-subtitle">
                            Add contextually relevant links to improve discovery and topical
                            authority.
                          </p>
                        </div>
                      </div>

                      <div className="seo-ai-suggestions-header-right">
                        <button
                          type="button"
                          className="seo-refresh-suggestions-btn"
                          onClick={handleGenerateRecommendations}
                          disabled={isGeneratingRecs}
                          data-testid="generate-recs-btn"
                        >
                          <i
                            className={`fas fa-wand-magic-sparkles ${isGeneratingRecs ? 'fa-spin' : ''}`}
                          ></i>
                          <span>{isGeneratingRecs ? 'Analyzing...' : 'Refresh suggestions'}</span>
                        </button>
                      </div>
                    </div>

                    <div className="seo-recommend-list" data-testid="recommendations-list">
                      {displayedRecommendations.length > 0 ? (
                        displayedRecommendations.map((rec) => {
                          const isOutboundRec =
                            !rec.targetId ||
                            rec.sourceId === selectedNode.id ||
                            rec.sourceNode === selectedNode.url;
                          const targetNodeObj = graph.nodes.find(
                            (n) =>
                              n.id === (isOutboundRec ? rec.targetId : rec.sourceId) ||
                              n.url === (isOutboundRec ? rec.targetNode : rec.sourceNode)
                          );
                          const targetTitle = isOutboundRec
                            ? targetNodeObj?.title || rec.targetTitle || rec.targetNode
                            : targetNodeObj?.title || rec.sourceTitle || rec.sourceNode;
                          const targetUrl = isOutboundRec
                            ? targetNodeObj?.url || rec.targetNode
                            : targetNodeObj?.url || rec.sourceNode;
                          const anchor = rec.suggestedAnchor || targetTitle;
                          const snippet = getRecommendationSnippet(
                            rec,
                            selectedNode,
                            targetNodeObj
                          );

                          return (
                            <div
                              key={rec.id}
                              className="seo-suggestion-card"
                              data-testid={`rec-card-${rec.id}`}
                            >
                              {/* Match Score Badge */}
                              <div className="seo-suggestion-match-badge">
                                <span className="seo-match-percent">
                                  {rec.relevanceScore || 90}%
                                </span>
                                <span className="seo-match-label">MATCH</span>
                              </div>

                              {/* Main Content */}
                              <div className="seo-suggestion-body">
                                <div className="seo-suggestion-target-header">
                                  <span className="seo-suggestion-link-to">
                                    {isOutboundRec ? 'LINK TO' : 'LINK FROM'}
                                  </span>
                                  <span className="seo-suggestion-target-title">{targetTitle}</span>
                                </div>

                                <div className="seo-suggestion-target-url">{targetUrl}</div>

                                <div className="seo-suggestion-snippet">
                                  {renderSnippetWithHighlight(snippet, anchor)}
                                </div>

                                <div className="seo-suggestion-meta">
                                  <span className="seo-suggestion-meta-label">
                                    Suggested anchor
                                  </span>
                                  <span className="seo-suggested-anchor-pill">{anchor}</span>
                                  {rec.loopSafe !== false ? (
                                    <span className="seo-loop-free-tag">
                                      <i className="fas fa-check"></i> Loop-free
                                    </span>
                                  ) : (
                                    <span className="seo-loop-danger-tag">
                                      <i className="fas fa-triangle-exclamation"></i> Potential
                                      circular loop
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="seo-suggestion-actions">
                                <button
                                  type="button"
                                  className="seo-suggestion-dismiss-btn"
                                  onClick={() => handleDismissRecommendation(rec.id)}
                                >
                                  Dismiss
                                </button>
                                <button
                                  type="button"
                                  className="seo-suggestion-add-btn"
                                  onClick={() => handleApplyRecommendation(rec)}
                                  data-testid={`apply-rec-btn-${rec.id}`}
                                >
                                  <i className="fas fa-plus"></i> Add link
                                </button>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="seo-no-suggestions-state">
                          <i className="fas fa-wand-magic-sparkles"></i>
                          <p>
                            No pending link suggestions for this view. Click "Refresh suggestions"
                            to analyze content topology and discover missing internal links.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* ACTIVE OUTGOING LINKS & ACTIONS (MAINTAINS FULL MANAGEMENT AND TESTING API) */}
                    <div className="seo-active-links-section">
                      <div className="seo-active-links-header">
                        <div className="seo-active-links-title-group">
                          <h4 className="seo-active-links-title">
                            <i className="fas fa-link text-blue"></i> Active Outgoing Links from
                            this Page ({nodeOutgoingEdges.length})
                          </h4>
                        </div>
                        <button
                          type="button"
                          className="seo-btn-add-manual-link"
                          onClick={() => setIsAddLinkOpen(true)}
                          data-testid="add-link-btn"
                        >
                          <i className="fas fa-plus"></i> Add Link
                        </button>
                      </div>

                      {nodeOutgoingEdges.length > 0 ? (
                        <table className="seo-mini-table" data-testid="outgoing-links-table">
                          <thead>
                            <tr>
                              <th>Target Page Node</th>
                              <th>Anchor Text</th>
                              <th style={{ width: '60px' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {nodeOutgoingEdges.map((edge, idx) => {
                              const targetNodeObj = graph.nodes.find((n) => n.id === edge.target);
                              return (
                                <tr key={idx} data-testid={`outgoing-link-row-${idx}`}>
                                  <td>
                                    <strong>{targetNodeObj?.title || edge.target}</strong>
                                    <br />
                                    <code className="text-xs">
                                      {targetNodeObj?.url || edge.target}
                                    </code>
                                  </td>
                                  <td>
                                    <span className="seo-anchor-badge">"{edge.anchor}"</span>
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="seo-icon-btn text-red"
                                      onClick={() => handleDeleteEdge(edge)}
                                      title="Delete internal link"
                                      data-testid={`delete-link-btn-${idx}`}
                                    >
                                      <i className="fas fa-trash-can"></i>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      ) : (
                        <p className="seo-subtle-hint" style={{ margin: '8px 0 0 0' }}>
                          No outgoing links recorded from this page yet.
                        </p>
                      )}

                      {nodeIncomingEdges.length > 0 && (
                        <div className="seo-incoming-links-wrapper" style={{ marginTop: '16px' }}>
                          <div
                            style={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#64748b',
                              marginBottom: '8px',
                            }}
                          >
                            <i className="fas fa-arrow-down-left text-green"></i> Pages Linking to
                            this Page ({nodeIncomingEdges.length}):
                          </div>
                          <div className="seo-incoming-links-list">
                            {nodeIncomingEdges.map((edge, idx) => {
                              const sourceNodeObj = graph.nodes.find((n) => n.id === edge.source);
                              return (
                                <div key={idx} className="seo-incoming-link-item">
                                  <span className="seo-incoming-source">
                                    {sourceNodeObj?.title || edge.source}
                                  </span>
                                  <span className="seo-incoming-anchor">"{edge.anchor}"</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="seo-empty-state">
                    <i className="fas fa-circle-nodes"></i>
                    <p>Select a node to inspect internal linking relationships and suggestions.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ==================================================================== */}
      {/* MODAL 1: ADD NEW PAGE NODE                                           */}
      {/* ==================================================================== */}
      {isAddNodeOpen && (
        <div className="seo-modal-overlay" onClick={() => setIsAddNodeOpen(false)}>
          <div className="seo-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="seo-modal-header">
              <div>
                <h3 className="seo-modal-title">
                  <i className="fas fa-plus-circle text-blue"></i> Add New Page to Site Graph
                </h3>
              </div>
              <button
                type="button"
                className="seo-modal-close-btn"
                onClick={() => setIsAddNodeOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleAddNode}>
              <div className="seo-modal-body">
                <div className="seo-field-group">
                  <label className="seo-field-label">Page Title</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="e.g. 2026 Guide to B2B Demand Gen"
                    required
                    value={newNodeTitle}
                    onChange={(e) => setNewNodeTitle(e.target.value)}
                    data-testid="new-node-title-input"
                  />
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Page URL Slug</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="e.g. /blog/demand-gen-guide"
                    required
                    value={newNodeUrl}
                    onChange={(e) => setNewNodeUrl(e.target.value)}
                    data-testid="new-node-url-input"
                  />
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Topic / Cluster</label>
                  <input
                    type="text"
                    list="topic-cluster-options"
                    className="seo-text-input"
                    placeholder="e.g. B2B Lead Enrichment, Cold Email Outreach..."
                    value={newNodeTopic}
                    onChange={(e) => setNewNodeTopic(e.target.value)}
                    data-testid="new-node-topic-input"
                  />
                  <datalist id="topic-cluster-options">
                    {(topicClusters || []).map((c) => (
                      <option key={c.id} value={c.topic} />
                    ))}
                  </datalist>
                </div>

                <div className="seo-field-row">
                  <div className="seo-field-group" style={{ flex: 1 }}>
                    <label className="seo-field-label">Page Type</label>
                    <select
                      className="seo-select-input"
                      value={newNodeType}
                      onChange={(e) => setNewNodeType(e.target.value)}
                    >
                      <option value="blog">Blog Article</option>
                      <option value="guide">In-Depth Guide</option>
                      <option value="product">Product Feature</option>
                      <option value="commercial">Commercial / Pricing</option>
                      <option value="external">External Website / Resource</option>
                      <option value="pillar">Pillar Hub</option>
                      <option value="resource">Resource</option>
                    </select>
                  </div>

                  <div className="seo-field-group" style={{ flex: 1 }}>
                    <label className="seo-field-label">Page Authority (1-100)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      className="seo-text-input"
                      value={newNodeAuthority}
                      onChange={(e) => setNewNodeAuthority(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="seo-modal-footer">
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={() => setIsAddNodeOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="seo-btn seo-btn-primary"
                  data-testid="save-node-btn"
                >
                  <i className="fas fa-check"></i> Save Page
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* MODAL 2: EDIT PAGE NODE                                              */}
      {/* ==================================================================== */}
      {isEditNodeOpen && selectedNode && (
        <div className="seo-modal-overlay" onClick={() => setIsEditNodeOpen(false)}>
          <div className="seo-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="seo-modal-header">
              <div>
                <h3 className="seo-modal-title">
                  <i className="fas fa-pen text-blue"></i> Edit Page Node
                </h3>
              </div>
              <button
                type="button"
                className="seo-modal-close-btn"
                onClick={() => setIsEditNodeOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveEditNode}>
              <div className="seo-modal-body">
                <div className="seo-field-group">
                  <label className="seo-field-label">Page Title</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    required
                    value={editNodeTitle}
                    onChange={(e) => setEditNodeTitle(e.target.value)}
                  />
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Page URL Slug</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    required
                    value={editNodeUrl}
                    onChange={(e) => setEditNodeUrl(e.target.value)}
                  />
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Topic / Cluster</label>
                  <input
                    type="text"
                    list="edit-topic-cluster-options"
                    className="seo-text-input"
                    placeholder="e.g. B2B Lead Enrichment, Cold Email Outreach..."
                    value={editNodeTopic}
                    onChange={(e) => setEditNodeTopic(e.target.value)}
                    data-testid="edit-node-topic-input"
                  />
                  <datalist id="edit-topic-cluster-options">
                    {(topicClusters || []).map((c) => (
                      <option key={c.id} value={c.topic} />
                    ))}
                  </datalist>
                </div>

                <div className="seo-field-row">
                  <div className="seo-field-group" style={{ flex: 1 }}>
                    <label className="seo-field-label">Page Type</label>
                    <select
                      className="seo-select-input"
                      value={editNodeType}
                      onChange={(e) => setEditNodeType(e.target.value)}
                    >
                      <option value="blog">Blog Article</option>
                      <option value="guide">In-Depth Guide</option>
                      <option value="product">Product Feature</option>
                      <option value="commercial">Commercial / Pricing</option>
                      <option value="external">External Website / Resource</option>
                      <option value="pillar">Pillar Hub</option>
                      <option value="resource">Resource</option>
                    </select>
                  </div>

                  <div className="seo-field-group" style={{ flex: 1 }}>
                    <label className="seo-field-label">Page Authority (1-100)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      className="seo-text-input"
                      value={editNodeAuthority}
                      onChange={(e) => setEditNodeAuthority(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="seo-modal-footer">
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={() => setIsEditNodeOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="seo-btn seo-btn-primary">
                  <i className="fas fa-save"></i> Update Page
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* MODAL 3: ADD INTERNAL LINK (WITH CIRCULAR LOOP CHECK)                */}
      {/* ==================================================================== */}
      {isAddLinkOpen && selectedNode && (
        <div className="seo-modal-overlay" onClick={() => setIsAddLinkOpen(false)}>
          <div className="seo-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="seo-modal-header">
              <div>
                <h3 className="seo-modal-title">
                  <i className="fas fa-link text-blue"></i> Add Internal Link
                </h3>
              </div>
              <button
                type="button"
                className="seo-modal-close-btn"
                onClick={() => setIsAddLinkOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleAddLink}>
              <div className="seo-modal-body">
                <div className="seo-field-group">
                  <label className="seo-field-label">Source Page</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    disabled
                    value={`${selectedNode.title} (${selectedNode.url})`}
                  />
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Target Page</label>
                  <select
                    className="seo-select-input"
                    required
                    value={newLinkTargetId}
                    onChange={(e) => setNewLinkTargetId(e.target.value)}
                    data-testid="target-node-select"
                  >
                    <option value="">-- Select Target Page Node --</option>
                    {graph.nodes
                      .filter((n) => n.id !== selectedNode.id)
                      .map((n) => (
                        <option key={n.id} value={n.id}>
                          {n.title} ({n.url})
                        </option>
                      ))}
                  </select>
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Anchor Text</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="e.g. verified B2B lead enrichment platform"
                    required
                    value={newLinkAnchor}
                    onChange={(e) => setNewLinkAnchor(e.target.value)}
                    data-testid="link-anchor-input"
                  />
                  <span className="seo-subtle-hint">
                    Use natural, descriptive keyword-rich anchor text rather than "click here".
                  </span>
                </div>

                {/* LOOP & EXCESSIVE LINKING VALIDATION WARNINGS */}
                {newLinkTargetId &&
                  graph.edges.some(
                    (e) => e.source === newLinkTargetId && e.target === selectedNode.id
                  ) && (
                    <div className="seo-loop-warning-box" data-testid="loop-warning-box">
                      <i className="fas fa-triangle-exclamation text-amber"></i>
                      <div>
                        <strong>Circular Link Warning:</strong> The selected target page already
                        links back to this source page. Creating this link will form a reciprocal
                        2-way circular loop.
                      </div>
                    </div>
                  )}

                {nodeOutgoingEdges.length >= 10 && (
                  <div className="seo-excessive-warning-box">
                    <i className="fas fa-triangle-exclamation text-amber"></i>
                    <div>
                      <strong>Excessive Linking Warning:</strong> This page already has{' '}
                      {nodeOutgoingEdges.length} outbound links. Keep total outbound links within
                      reasonable limits to avoid PageRank dilution.
                    </div>
                  </div>
                )}
              </div>

              <div className="seo-modal-footer">
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={() => setIsAddLinkOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="seo-btn seo-btn-primary"
                  data-testid="save-link-btn"
                >
                  <i className="fas fa-link"></i> Add Link
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
